import { Component, Inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MessageService } from '../../../services/message-service/message.service';
import { GruppeTier } from '../classes/gruppe-tier';
import { GruppeTierService } from '../gruppe-tier.service';

export interface GruppeTierGewichteFestlegenDialogInput {
    gruppeId: number;
    gruppeTiere: GruppeTier[];
}

@Component({
    selector: 'kiene-gruppe-tier-gewichte-festlegen-dialog',
    templateUrl: './gruppe-tier-gewichte-festlegen-dialog.component.html',
    styleUrls: ['./gruppe-tier-gewichte-festlegen-dialog.component.scss'],
})
export class GruppeTierGewichteFestlegenDialogComponent {
    einstallgewichtCtrl = new FormControl<number | null>(null, [Validators.min(0.000001)]);
    ausstallgewichtCtrl = new FormControl<number | null>(null, [Validators.min(0.000001)]);
    isLoading = false;
    submitAttempted = false;

    constructor(
        public dialogRef: MatDialogRef<GruppeTierGewichteFestlegenDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: GruppeTierGewichteFestlegenDialogInput,
        private gruppeTierService: GruppeTierService,
        private messageService: MessageService,
    ) {
        const vorhandenesAusstallgewicht = this.vorhandenesAusstallgewicht();
        if (vorhandenesAusstallgewicht != null) {
            this.ausstallgewichtCtrl.setValue(vorhandenesAusstallgewicht);
        }
    }

    /** Nur bei einem Tier: das in der Liste gezeigte Ausstallgewicht. */
    private vorhandenesAusstallgewicht(): number | null {
        const tiere = this.data?.gruppeTiere ?? [];
        if (tiere.length !== 1) {
            return null;
        }
        const wert = Number(tiere[0]?.ausstallgewicht);
        if (!Number.isFinite(wert) || wert <= 0) {
            return null;
        }
        return wert;
    }

    get anzahlTiere(): number {
        return this.data?.gruppeTiere?.length || 0;
    }

    private hasValue(value: number | null): boolean {
        return value !== null && value !== undefined;
    }

    get hasAnyGewichtValue(): boolean {
        return this.hasValue(this.einstallgewichtCtrl.value) || this.hasValue(this.ausstallgewichtCtrl.value);
    }

    get noValueErrorVisible(): boolean {
        return this.submitAttempted && !this.hasAnyGewichtValue;
    }

    close(): void {
        this.dialogRef.close();
    }

    save(): void {
        this.submitAttempted = true;
        this.einstallgewichtCtrl.markAsTouched();
        this.ausstallgewichtCtrl.markAsTouched();

        if (!this.hasAnyGewichtValue) {
            this.messageService.errorMessage('Bitte geben Sie mindestens ein Gewicht ein.', null);
            return;
        }

        if (this.einstallgewichtCtrl.invalid || this.ausstallgewichtCtrl.invalid) {
            this.messageService.errorMessage('Gewichtswerte muessen groesser als 0 sein.', null);
            return;
        }

        const gruppeTiere = this.data?.gruppeTiere ?? [];
        const gruppeId = Number(this.data?.gruppeId);

        if (!this.hasAnyGewichtValue) {
            this.messageService.errorMessage('Bitte geben Sie mindestens ein Gewicht ein.', null);
            return;
        }

        this.isLoading = true;
        this.gruppeTierService
            .saveGewichteFestlegen(
                gruppeId,
                gruppeTiere,
                this.einstallgewichtCtrl.value,
                this.ausstallgewichtCtrl.value,
            )
            .subscribe({
            next: () => {
                const text =
                    gruppeTiere.length === 1
                        ? 'Gewicht wurde erfolgreich gespeichert.'
                        : 'Gewichte fuer selektierte Tiere wurden erfolgreich gespeichert.';
                this.messageService.infoMessage(text);
                this.dialogRef.close(true);
            },
            error: err => {
                this.messageService.errorMessage('Fehler beim Speichern der Gewichte.', err);
                this.isLoading = false;
            },
        });
    }
}
